import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  FileText,
  Truck,
  Calendar,
  CreditCard,
  Loader2,
  ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobTimelineModal({ open, onClose, jobId }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && jobId) {
      loadTimeline();
    }
  }, [open, jobId]);

  const loadTimeline = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/phs/core/jobs/${jobId}/timeline`, { headers });
      setTimeline(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'status_changed': <ChevronRight className="h-4 w-4" />,
      'created': <FileText className="h-4 w-4" />,
      'assigned': <User className="h-4 w-4" />,
      'scheduled': <Calendar className="h-4 w-4" />,
      'payment_received': <CreditCard className="h-4 w-4" />,
      'dispatched': <Truck className="h-4 w-4" />,
      'completed': <CheckCircle className="h-4 w-4" />,
    };
    return icons[eventType] || <Clock className="h-4 w-4" />;
  };

  const getEventColor = (action) => {
    const colors = {
      'status_changed': 'bg-blue-100 text-blue-600',
      'created': 'bg-green-100 text-green-600',
      'assigned': 'bg-purple-100 text-purple-600',
      'scheduled': 'bg-cyan-100 text-cyan-600',
      'payment_received': 'bg-emerald-100 text-emerald-600',
      'dispatched': 'bg-orange-100 text-orange-600',
      'completed': 'bg-green-100 text-green-600',
    };
    return colors[action] || 'bg-gray-100 text-gray-600';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Combine and deduplicate events
  const getAllEvents = () => {
    if (!timeline) return [];
    
    const events = [];
    
    // Add job_events collection events
    if (timeline.events) {
      timeline.events.forEach(e => {
        events.push({
          id: e.event_id || e._id,
          action: e.action,
          description: e.note || `${e.previous_status} → ${e.new_status}`,
          timestamp: e.timestamp,
          user: e.user_name,
          type: 'audit',
          metadata: e
        });
      });
    }
    
    // Add embedded timeline events
    if (timeline.embedded_timeline) {
      timeline.embedded_timeline.forEach((e, idx) => {
        events.push({
          id: e.id || `embedded-${idx}`,
          action: e.event_type || 'update',
          description: e.description,
          timestamp: e.timestamp,
          user: e.user?.name || e.user,
          type: 'embedded'
        });
      });
    }
    
    // Sort by timestamp descending and deduplicate by ID
    const seen = new Set();
    return events
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="job-timeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#0F766E]" />
            Job Timeline
            {timeline?.job_reference && (
              <Badge variant="outline" className="ml-2 font-mono">
                {timeline.job_reference}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0F766E]" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-2" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <ScrollArea className="h-96 pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
              
              {/* Events */}
              <div className="space-y-4">
                {getAllEvents().map((event, idx) => (
                  <div key={event.id} className="flex gap-4">
                    {/* Icon */}
                    <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center ${getEventColor(event.action)}`}>
                      {getEventIcon(event.action)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <div className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <Badge variant="outline" className="text-xs capitalize">
                            {event.action?.replace(/_/g, ' ')}
                          </Badge>
                          {event.type === 'audit' && (
                            <span className="text-[10px] text-[#0F766E] font-medium">IMMUTABLE</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-800">{event.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <User className="h-3 w-3" />
                          <span>{event.user || 'System'}</span>
                          <span className="text-gray-300">•</span>
                          <Clock className="h-3 w-3" />
                          <span>{formatTimestamp(event.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {getAllEvents().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No timeline events recorded yet
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
